import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResendVerificationPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('form'); // form, sent, already_verified
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email address');
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
            
            if (res.data.already_verified) {
                setStatus('already_verified');
                toast.info('Your email is already verified!');
            } else if (res.data.sent) {
                setStatus('sent');
                toast.success('Verification email sent! Check your inbox and spam folder.');
            } else {
                // Generic response (user might not exist)
                setStatus('sent');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to send verification email';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (status === 'already_verified') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/30">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                            <CheckCircle className="h-8 w-8 text-green-500" />
                        </div>
                        <CardTitle className="text-green-500">Already Verified!</CardTitle>
                        <CardDescription>
                            Good news! Your email <strong>{email}</strong> is already verified.
                            You can log in now.
                        </CardDescription>
                    </CardHeader>
                    <CardFooter>
                        <Button className="w-full" onClick={() => navigate('/login')}>
                            Continue to Login
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    if (status === 'sent') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/30">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                            <Mail className="h-8 w-8 text-green-500" />
                        </div>
                        <CardTitle>Check Your Email</CardTitle>
                        <CardDescription>
                            If an account exists for <strong>{email}</strong>, we've sent a new verification link.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20 text-sm">
                            <div className="flex gap-2">
                                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-amber-500">Can't find the email?</p>
                                    <ul className="text-muted-foreground mt-1 space-y-1">
                                        <li>• Check your <strong>spam/junk</strong> folder</li>
                                        <li>• Make sure you entered the correct email</li>
                                        <li>• Wait a few minutes and refresh your inbox</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3">
                        <Button variant="outline" className="w-full" onClick={() => setStatus('form')}>
                            Try Different Email
                        </Button>
                        <Link to="/login" className="w-full">
                            <Button className="w-full">Back to Login</Button>
                        </Link>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/30">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                        <Mail className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle>Resend Verification Email</CardTitle>
                    <CardDescription>
                        Enter your email address and we'll send you a new verification link.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3">
                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Verification Email'
                            )}
                        </Button>
                        <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                            Back to Login
                        </Link>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
